import { FiActivity } from 'react-icons/fi';

export const diagramLabellingData = {
  courseId: 'diagram-labelling',
  courseType: 'reading-course',
  title: 'Diagram Labelling',
  icon: FiActivity,
  overview: {
    impact: 'Label parts of a process, cycle or technical diagram from a passage',
    focus: 'Process language, prepositions of place, sequence and structure',
    uses: 'Reading and Listening (the same task type appears in both)',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'What Diagram Labelling Tests',
      content:
        '<p>You are given a <strong>diagram</strong> (a process, a cycle, a piece of equipment, a building plan, or a map) with empty boxes. You must fill those boxes with <strong>1-3 words</strong> taken from the passage. The goal is to test whether you can match a written description to a visual representation.</p>',
      points: [
        'Each label has a strict word limit (commonly NO MORE THAN TWO / THREE WORDS).',
        'Words must come exactly as they appear in the passage — no paraphrase.',
        'Spelling counts. A misspelled label is wrong.',
      ],
      warning:
        'Do not invent or paraphrase a label. Use the exact word(s) from the passage. Plurals matter (data vs datum, samples vs sample).',
    },
    {
      title: 'Read the Diagram First',
      content:
        '<p>The diagram itself contains crucial clues — labels you already know, arrows, sequences, and where the gaps appear in the flow.</p>',
      points: [
        'Identify what kind of diagram it is: a process, a cycle, a cross-section, or a map.',
        'Read all existing labels to understand the topic and the shared vocabulary field (e.g., water, energy, manufacturing).',
        'Mark where each gap sits: at the start, middle, end, or as an output.',
        'Note the arrows: do they show direction, transformation, or location?',
      ],
    },
    {
      title: 'Locate the Right Section in the Passage',
      content:
        '<p>The passage typically describes the diagram in <strong>order</strong>, but using different words from the labels you already see.</p>',
      points: [
        'Use existing labels as anchors to find the matching paragraph.',
        'Watch for sequence words: "first", "then", "next", "after", "finally".',
        'Watch for transformation language: "is heated", "becomes", "turns into", "is converted".',
        'Once located, scan one sentence at a time around the gap; the answer is rarely far from your anchor.',
      ],
    },
    {
      title: 'Common Word-Limit Mistakes',
      content: '<p>Most lost points come from word-limit and form errors:</p>',
      table: {
        headers: ['Issue', 'Example', 'Fix'],
        rows: [
          ['Too many words', '"the heat exchanger system" with 2-word limit', 'Use "heat exchanger".'],
          ['Wrong form', 'Writing "filter" when the passage says "filtered water"', 'Match the exact noun phrase used.'],
          ['Article slip', 'Writing "a turbine" when only "turbine" was in the passage', 'Drop "a/an/the" unless it is part of a fixed phrase.'],
          ['Plural slip', 'Writing "sample" when the passage says "samples"', 'Match singular/plural exactly.'],
          ['Paraphrasing', 'Writing "machine" when the passage says "device"', 'Always copy the exact word from the passage.'],
        ],
      },
    },
  ],
  exercises: [
    {
      title: 'Process Description Match',
      type: 'fill-in',
      instructions:
        'Read the description. Fill the gap with a word or phrase that fits the diagram (NO MORE THAN TWO WORDS).',
      questions: [
        {
          text:
            'Passage: "Water is first pumped from the underground reservoir into a holding tank, where it is filtered to remove particles. From the filter, the water flows into a heating chamber and is then released as steam to drive the turbine."\nDiagram step: "From the holding tank, water passes through the ____ before entering the heating chamber."\nAnswer (no more than two words):',
          answer: 'filter',
          explanation:
            '"Filter" is the device sitting between the holding tank and the heating chamber. The passage names it explicitly.',
        },
        {
          text:
            'Passage: "Water is first pumped from the underground reservoir into a holding tank, where it is filtered to remove particles. From the filter, the water flows into a heating chamber and is then released as steam to drive the turbine."\nDiagram step: "Inside the heating chamber, water becomes ____ which then drives the turbine."\nAnswer (no more than two words):',
          answer: 'steam',
          explanation:
            'The passage explicitly names "steam" as the output of the heating chamber.',
        },
        {
          text:
            'Passage: "Sunlight enters the cell and energises a layer of silicon. Free electrons are released and travel through a copper wire to a small battery."\nDiagram step: "Sunlight strikes the ____ layer, releasing electrons."\nAnswer (no more than two words):',
          answer: 'silicon',
          explanation:
            'The passage states that silicon is the layer energised by sunlight, releasing electrons.',
        },
      ],
    },
    {
      title: 'Cycle and Cross-Section',
      type: 'fill-in',
      instructions:
        'Use the passage to fill the diagram label with NO MORE THAN THREE WORDS.',
      questions: [
        {
          text:
            'Passage: "Water evaporates from the ocean surface and rises into the atmosphere. Once cooled, vapour condenses into clouds. Clouds eventually release water as rain or snow, which returns to rivers and finally to the ocean."\nDiagram label (between "atmosphere" and "rain"): "____"\nAnswer (no more than three words):',
          answer: 'clouds',
          explanation:
            'The passage states "vapour condenses into clouds" — clouds sit between atmosphere and rainfall.',
        },
        {
          text:
            'Passage: "Inside the leaf, sunlight is absorbed by chlorophyll. Carbon dioxide enters through tiny pores called stomata. Water arrives from the roots through xylem vessels."\nDiagram label (the small openings on the leaf surface): "____"\nAnswer (no more than three words):',
          answer: 'stomata',
          explanation:
            'The passage explicitly names the small openings as "stomata".',
        },
      ],
    },
    {
      title: 'Map and Plan Labels',
      type: 'select',
      instructions: 'Choose the correct word from the passage.',
      questions: [
        {
          text:
            'Passage: "Visitors enter through the main gate, walk past the fountain on the left, and reach the information desk before climbing the staircase to the exhibition hall."\nLabel: "After the fountain, visitors arrive at the ____."\nWhich word fits?',
          options: ['exhibition hall', 'staircase', 'information desk'],
          answer: 'information desk',
          explanation:
            'The passage states they reach the information desk before the staircase.',
        },
        {
          text:
            'Passage: "The new wing of the museum is to the north of the courtyard, while the gift shop sits directly opposite the main gate."\nLabel: "Across from the main gate stands the ____."\nWhich word fits?',
          options: ['gift shop', 'courtyard', 'museum wing'],
          answer: 'gift shop',
          explanation:
            'The passage states the gift shop is "directly opposite the main gate".',
        },
      ],
    },
    {
      title: 'Spot the Word-Limit Trap',
      type: 'select',
      instructions:
        'The word limit is "NO MORE THAN TWO WORDS". Choose the correct label.',
      questions: [
        {
          text:
            'Passage: "Inside the chamber, the gas is compressed by a powerful electric pump."\nLabel: "Component compressing the gas: ____"\nWhich answer respects the 2-word limit?',
          options: ['the powerful electric pump', 'electric pump', 'pump'],
          answer: 'electric pump',
          explanation:
            '"electric pump" is exactly 2 words and matches the passage. "the powerful electric pump" is 4 words and would be marked wrong.',
        },
      ],
    },
  ],
  tips: [
    'Read existing diagram labels first — they tell you the topic and the vocabulary field.',
    'Stay within the word limit. Count your words before locking each answer.',
    'Copy the exact word(s) from the passage. Do not paraphrase.',
    'Watch singular vs plural and any small spelling differences.',
    'Use sequence words ("first / then / next") and transformation verbs ("is heated", "becomes") as anchors.',
  ],
  dailyPractice: [
    'Find a science article that describes a process and try to draw a diagram from it.',
    'Practise labelling 3 process diagrams a week with strict word limits.',
    'Copy 10 sentences from a process description and underline the key noun in each.',
    'Compare two diagrams (a cycle vs a process) and list the linguistic differences.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Diagram Labelling: matching a passage to a process, cycle, cross-section or map by extracting exact words within strict word limits, with attention to plurals, singulars and spelling.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...diagramLabellingData,
      title: diagramLabellingData.title + ' (Foundations)',
      overview: { ...diagramLabellingData.overview, duration: '15-20 minutes' },
      sections: diagramLabellingData.sections.slice(0, 2),
      exercises: [
        {
          ...diagramLabellingData.exercises[0],
          title: 'Process Description Match (Basic)',
          questions: diagramLabellingData.exercises[0].questions.slice(0, 2),
        },
        {
          ...diagramLabellingData.exercises[3],
          title: 'Spot the Word-Limit Trap (Basic)',
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...diagramLabellingData,
      title: diagramLabellingData.title + ' (Advanced)',
      overview: { ...diagramLabellingData.overview, duration: '25-30 minutes' },
      sections: [
        ...diagramLabellingData.sections,
        {
          title: 'When Two Words Both Seem Right',
          content:
            '<p>At advanced level, the trap is usually a near-synonym. The passage may use both "container" and "tank" — but only one labels the diagram.</p>',
          points: [
            'Re-read the sentence in the passage that explicitly describes that part of the diagram.',
            'Prefer the most specific noun: "turbine" is more specific than "machine".',
            'If two synonyms appear, check the surrounding label words — the answer must be consistent with the diagram\'s level of detail.',
          ],
        },
      ],
      exercises: [
        ...diagramLabellingData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
      ],
      tips: [
        ...(diagramLabellingData.tips || []),
        'At Band 8+, when two words seem right, prefer the most specific term consistent with neighbouring labels.',
      ],
    };
  }
  return diagramLabellingData;
}
